/**
 * Hreflang Service
 * Generates hreflang tags for international SEO
 */

export class HreflangService {
	constructor() {
		this.baseUrl = this.getBaseUrl();
		this.availableLanguages = this.getAvailableLanguages();
	}

	/**
	 * Get the base URL for the application
	 */
	getBaseUrl() {
		if (typeof window !== "undefined") {
			return `${window.location.protocol}//${window.location.host}`;
		}
		return "https://gps.solenis.com";
	}

	/**
	 * Get available languages from the application
	 * This should match the languages in your sitemap (SELECT two_digit_code FROM language WHERE active=1 ORDER BY two_digit_code;)
	 */
	getAvailableLanguages() {
		return ["de", "en", "es", "fr", "it", "no", "pl", "pt", "sv", "tr"];
	}

	/**
	 * Generate hreflang tags for the current route
	 * @param {Object} route - Vue Router route object
	 * @param {Array} languages - Available languages from store
	 * @returns {Array} Array of hreflang objects
	 */
	generateHreflangTags(route, languages = null) {
		const availableLanguages = languages || this.availableLanguages;
		const hreflangTags = [];

		// Get the current path without language prefix
		const currentPath = this.getPathWithoutLanguage(route.path);

		// Add hreflang tag for each available language
		availableLanguages.forEach((lang) => {
			const url = `${this.baseUrl}/${lang}${currentPath}`;
			hreflangTags.push({
				rel: "alternate",
				hreflang: lang,
				href: url,
			});
		});

		// Add x-default hreflang tag (usually points to English or main language)
		const defaultUrl = `${this.baseUrl}/en${currentPath}`;
		hreflangTags.push({
			rel: "alternate",
			hreflang: "x-default",
			href: defaultUrl,
		});

		return hreflangTags;
	}

	/**
	 * Remove language prefix from path to get the base path
	 * @param {string} path - Current route path
	 * @returns {string} Path without language prefix
	 */
	getPathWithoutLanguage(path) {
		// Remove leading slash and language code
		const pathParts = path.split("/").filter((part) => part);

		// Check if first part is a language code
		if (
			pathParts.length > 0 &&
			this.availableLanguages.includes(pathParts[0])
		) {
			// Remove the language part
			pathParts.shift();
		}

		// Reconstruct path
		return pathParts.length > 0 ? `/${pathParts.join("/")}` : "";
	}

	/**
	 * Inject hreflang tags into the document head
	 * @param {Array} hreflangTags - Array of hreflang tag objects
	 */
	injectHreflangTags(hreflangTags) {
		// Remove existing hreflang tags
		this.removeExistingHreflangTags();

		// Add new hreflang tags
		hreflangTags.forEach((tag) => {
			const link = document.createElement("link");
			link.rel = tag.rel;
			link.hreflang = tag.hreflang;
			link.href = tag.href;
			document.head.appendChild(link);
		});
	}

	/**
	 * Remove existing hreflang tags from document head
	 */
	removeExistingHreflangTags() {
		const existingTags = document.querySelectorAll("link[hreflang]");
		existingTags.forEach((tag) => tag.remove());
	}

	/**
	 * Generate hreflang tags for a specific route and inject them
	 * @param {Object} route - Vue Router route object
	 * @param {Array} languages - Available languages from store
	 */
	generateAndInjectHreflangTags(route, languages = null) {
		const hreflangTags = this.generateHreflangTags(route, languages);
		this.injectHreflangTags(hreflangTags);
		return hreflangTags;
	}
}

// Export singleton instance
export const hreflangService = new HreflangService();
